import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Camera,
  CheckSquare,
  ClipboardList,
  FileQuestion,
  ImagePlus,
  MessageSquareWarning,
  Plus,
  Save,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createOpname,
  createWorkorderFromOpname,
  getOpname,
  getOpnames,
  updateOpname,
  uploadOpnamePhoto,
} from "../api/opnames";
import api from "../services/api";
import { useUser } from "../context/UserContext";
import {
  createOpnameDraftId,
  createMonteurScopeKey,
  deletePendingOpnamePhoto,
  deleteDraftOpname,
  getCachedLookupClients,
  getCachedLookupProjects,
  getCachedLookupWorkorders,
  getCachedOpnameDetail,
  getCachedOpnames,
  getDraftOpname,
  getPendingOpnamePhoto,
  putPendingOpnamePhoto,
  setCachedLookupClients,
  setCachedLookupProjects,
  setCachedLookupWorkorders,
  setCachedOpnameDetail,
  setCachedOpnames,
  setDraftOpname,
} from "../services/opnameOfflineService";
import {
  cleanupResolvedPendingOpnamePhotos,
  clearQueuedOpnameMutations,
  createOpnameClientMutationId,
  createLocalOpnameId,
  isLocalOpnameId,
  listQueuedOpnameMutations,
  queueOpnameMutation,
  resolvePendingOpnamePhotoUploads,
  runQueuedOpnameSync,
  upsertOfflineOpnameSnapshot,
} from "../services/opnameSyncService";

const ITEM_TYPES = [
  { key: "vraag", label: "Vraag + antwoord", icon: FileQuestion },
  { key: "constatering", label: "Constatering", icon: MessageSquareWarning },
  { key: "actiepunt", label: "Actiepunt", icon: ClipboardList },
  { key: "foto", label: "Foto-item", icon: Camera },
  { key: "checklist", label: "Checklist", icon: CheckSquare },
];

const PRIMARY_ITEM_TYPE_KEYS = ["constatering", "actiepunt"];

const DEFAULT_OPNAME = {
  pendingPhotoCleanupIds: [],
  sourceRequestId: "",
  titel: "",
  omschrijving: "",
  projectNummer: "",
  clientId: "",
  projectId: "",
  workorderId: "",
  discipline: "bmi",
  aardWerk: "nieuw",
  uitvoervorm: "onbekend",
  locatie: {
    bronpunt: "",
    bestemming: "",
    ruimte: "",
    omgeving: "",
  },
  onzekerheden: "",
  status: "concept",
  items: [],
};

function getApiOrigin() {
  const baseUrl = String(api?.defaults?.baseURL || "").trim();
  if (!baseUrl) return "";
  return baseUrl.replace(/\/api\/?$/, "");
}

function resolveAssetUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;

  const apiOrigin = getApiOrigin();
  if (!apiOrigin) {
    if (raw.startsWith("/")) return raw;
    if (raw.includes("/")) return `/${raw.replace(/^\/+/, "")}`;
    return raw;
  }

  if (raw.startsWith("/")) return `${apiOrigin}${raw}`;
  if (raw.includes("/")) return `${apiOrigin}/${raw.replace(/^\/+/, "")}`;
  return raw;
}

function buildStablePhotoUrl(fileId, fallbackUrl = "") {
  const normalizedFileId = String(fileId || "").trim();
  if (normalizedFileId) {
    return `/api/files/${normalizedFileId}/view`;
  }
  const rawUrl = String(fallbackUrl || "").trim();
  if (!rawUrl) return "";
  return rawUrl.split("#")[0].split("?")[0].trim();
}

function normalizeOpnamePhoto(photo) {
  return {
    fileId: String(photo?.fileId || "").trim(),
    url: String(photo?.url || photo?.fileUrl || photo?.viewUrl || "").trim(),
    localPhotoId: String(photo?.localPhotoId || "").trim(),
    pendingUpload: photo?.pendingUpload === true || Boolean(String(photo?.localPhotoId || "").trim()),
    fileName: String(photo?.fileName || "").trim(),
    previewUrl: resolveAssetUrl(
      photo?.previewUrl || photo?.thumbnailUrl || photo?.url || photo?.fileUrl || photo?.viewUrl || ""
    ),
    caption: String(photo?.caption || "").trim(),
  };
}

function isSecureFileUrl(value) {
  return /^\/api\/files\/[^/]+\/view$/i.test(String(value || "").trim());
}

function createLocalPhotoId() {
  return `photo:${Date.now()}:${cryptoRandomId()}`;
}

function isRetryableUploadError(error) {
  const status = Number(error?.status || error?.response?.status || 0);
  if (!status) return true;
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

async function hydrateOpnameForForm(data, scopeKey) {
  const normalized = normalizeOpname(data);
  const nextItems = await Promise.all(
    normalized.items.map(async (item) => {
      const nextPhotos = await Promise.all(
        (item.photos || []).map(async (photo) => {
          const localPhotoId = String(photo?.localPhotoId || "").trim();
          if (!localPhotoId || photo.previewUrl) return photo;
          const pendingPhoto = await getPendingOpnamePhoto({ scopeKey, photoId: localPhotoId });
          if (!pendingPhoto?.blob) return photo;
          return {
            ...photo,
            previewUrl: URL.createObjectURL(pendingPhoto.blob),
            fileName: photo.fileName || pendingPhoto.fileName || "",
            pendingUpload: true,
          };
        })
      );
      return {
        ...item,
        photos: nextPhotos,
      };
    })
  );

  return {
    ...normalized,
    items: nextItems,
  };
}

function createDefaultOpname() {
  return {
    ...DEFAULT_OPNAME,
    sourceRequestId: createOpnameClientMutationId(),
    items: [createEmptyItem("constatering")],
  };
}

export default function OpnameWerkbon() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const monteurScopeKey = useMemo(() => createMonteurScopeKey(user), [user]);
  const [opnames, setOpnames] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workorders, setWorkorders] = useState([]);
  const [selectedOpnameId, setSelectedOpnameId] = useState("");
  const [form, setForm] = useState(createDefaultOpname());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingWorkorder, setCreatingWorkorder] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadingItemId, setUploadingItemId] = useState("");
  const [showMoreTypes, setShowMoreTypes] = useState(false);
  const [draftStatus, setDraftStatus] = useState("idle");
  const [draftUpdatedAt, setDraftUpdatedAt] = useState("");
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [syncStatus, setSyncStatus] = useState("idle");
  const [pendingSyncCount, setPendingSyncCount] = useState(0);

  const selectedOpname = useMemo(
    () => opnames.find((item) => String(item._id) === String(selectedOpnameId)) || null,
    [opnames, selectedOpnameId]
  );

  const primaryTypes = useMemo(
    () => ITEM_TYPES.filter((item) => PRIMARY_ITEM_TYPE_KEYS.includes(item.key)),
    []
  );

  const secondaryTypes = useMemo(
    () => ITEM_TYPES.filter((item) => !PRIMARY_ITEM_TYPE_KEYS.includes(item.key)),
    []
  );

  const roleNames = useMemo(() => getRoleNames(user), [user]);
  const permissionKeys = useMemo(
    () =>
      Array.isArray(user?.permissions)
        ? user.permissions.map((item) => String(item || "").trim().toLowerCase()).filter(Boolean)
        : [],
    [user]
  );
  const canCreateWorkorderFromOpname = useMemo(() => {
    if (permissionKeys.includes("workorder.create")) return true;
    return roleNames.some((role) =>
      ["projectleider", "tenantadmin", "werkvoorbereider", "admin", "controller"].includes(role)
    );
  }, [permissionKeys, roleNames]);

  const availableProjects = useMemo(() => {
    const selectedClientId = String(form.clientId || "").trim();
    if (!selectedClientId) return projects;
    return projects.filter(
      (project) =>
        String(project?.clientId?._id || project?.clientId || "").trim() === selectedClientId
    );
  }, [projects, form.clientId]);

  const availableWorkorders = useMemo(() => {
    const selectedClientId = String(form.clientId || "").trim();
    const selectedProjectId = String(form.projectId || "").trim();

    return workorders.filter((workorder) => {
      const workorderClientId = String(workorder?.clientId?._id || workorder?.clientId || "").trim();
      const workorderProjectId = String(workorder?.projectId?._id || workorder?.projectId || "").trim();

      if (selectedProjectId) return workorderProjectId === selectedProjectId;
      if (selectedClientId) return workorderClientId === selectedClientId;
      return true;
    });
  }, [workorders, form.clientId, form.projectId]);

  const activeDraftId = useMemo(() => createOpnameDraftId(selectedOpnameId), [selectedOpnameId]);

  const refreshQueueState = useCallback(async () => {
    const queue = await listQueuedOpnameMutations({ scopeKey: monteurScopeKey });
    const activeItems = queue.filter((item) => String(item?.status || "") !== "done");
    setPendingSyncCount(activeItems.length);
    return activeItems;
  }, [monteurScopeKey]);

  const loadOpnames = useCallback(async () => {
    const applyOfflineStatuses = async (items) => {
      const queue = await refreshQueueState();
      const pendingByEntityId = new Set(queue.map((item) => String(item?.entityId || "").trim()).filter(Boolean));
      const nextItems = (Array.isArray(items) ? items : []).map((item) => ({
        ...item,
        _offlineStatus: pendingByEntityId.has(String(item?._id || "").trim()) ? "pending_sync" : "",
      }));

      for (const queueItem of queue) {
        const entityId = String(queueItem?.entityId || "").trim();
        if (!entityId) continue;
        const exists = nextItems.some((item) => String(item?._id || "").trim() === entityId);
        if (exists) continue;
        const [draft, cachedDetail] = await Promise.all([
          getDraftOpname({
            scopeKey: monteurScopeKey,
            draftId: createOpnameDraftId(entityId),
          }),
          getCachedOpnameDetail({
            scopeKey: monteurScopeKey,
            opnameId: entityId,
          }),
        ]);
        const fallbackPayload = draft?.payload || cachedDetail?.payload || null;
        if (!fallbackPayload) continue;
        nextItems.unshift({
          ...fallbackPayload,
          _id: entityId,
          _offlineStatus: "pending_sync",
        });
      }

      return nextItems;
    };

    try {
      setLoading(true);
      setError("");
      const data = await getOpnames();
      const nextOpnames = await applyOfflineStatuses(Array.isArray(data) ? data : []);
      setOpnames(nextOpnames);
      await setCachedOpnames({
        scopeKey: monteurScopeKey,
        cacheKey: "mine",
        payload: nextOpnames,
      });
    } catch (err) {
      const cached = await getCachedOpnames({
        scopeKey: monteurScopeKey,
        cacheKey: "mine",
      });
      const cachedOpnames = await applyOfflineStatuses(
        Array.isArray(cached?.payload) ? cached.payload : []
      );
      if (cachedOpnames.length > 0) {
        setOpnames(cachedOpnames);
        setError("");
      } else {
        setError(err.message || "Opnames laden mislukt");
      }
    } finally {
      setLoading(false);
    }
  }, [monteurScopeKey, refreshQueueState]);

  const openOpname = useCallback(async (id) => {
    const targetId = String(id || "").trim();
    if (!targetId) return;

    try {
      setLoading(true);
      setError("");
      const data = await getOpname(targetId);
      const draft = await getDraftOpname({
        scopeKey: monteurScopeKey,
        draftId: createOpnameDraftId(targetId),
      });
      setSelectedOpnameId(String(data._id));
      setForm(await hydrateOpnameForForm(draft?.payload || data, monteurScopeKey));
      setSuccess("");
      setDraftStatus(draft?.payload ? "draft" : "idle");
      setDraftUpdatedAt(String(draft?.updatedAt || "").trim());
      setDraftHydrated(true);
      await setCachedOpnameDetail({
        scopeKey: monteurScopeKey,
        opnameId: targetId,
        payload: data,
      });
    } catch (err) {
      const cached = await getCachedOpnameDetail({
        scopeKey: monteurScopeKey,
        opnameId: targetId,
      });
      const draft = await getDraftOpname({
        scopeKey: monteurScopeKey,
        draftId: createOpnameDraftId(targetId),
      });
      if (draft?.payload || cached?.payload) {
        setSelectedOpnameId(targetId);
        setForm(await hydrateOpnameForForm(draft?.payload || cached.payload, monteurScopeKey));
        setError("");
        setSuccess("");
        setDraftStatus(draft?.payload ? "draft" : "idle");
        setDraftUpdatedAt(String(draft?.updatedAt || "").trim());
        setDraftHydrated(true);
      } else {
        setError(err.message || "Opname laden mislukt");
      }
    } finally {
      setLoading(false);
    }
  }, [monteurScopeKey]);

  const refreshSelectedOpname = useCallback(
    async (opnameId = selectedOpnameId) => {
      const targetId = String(opnameId || "").trim();
      if (!targetId) return;
      const data = await getOpname(targetId);
      setSelectedOpnameId(String(data._id));
      setForm(await hydrateOpnameForForm(data, monteurScopeKey));
      await setCachedOpnameDetail({
        scopeKey: monteurScopeKey,
        opnameId: targetId,
        payload: data,
      });
      return data;
    },
    [monteurScopeKey, selectedOpnameId]
  );

  useEffect(() => {
    loadOpnames();
  }, [loadOpnames]);

  useEffect(() => {
    let active = true;

    async function hydrateNewDraft() {
      if (selectedOpnameId) return;
      const params = new URLSearchParams(location.search || "");
      const opnameId = String(params.get("opnameId") || "").trim();
      if (opnameId) return;

      const draft = await getDraftOpname({
        scopeKey: monteurScopeKey,
        draftId: createOpnameDraftId(""),
      });
      if (!active) return;

      if (draft?.payload) {
        setForm(await hydrateOpnameForForm(draft.payload, monteurScopeKey));
        setDraftStatus("draft");
        setDraftUpdatedAt(String(draft?.updatedAt || "").trim());
      } else {
        setForm(createDefaultOpname());
        setDraftStatus("idle");
        setDraftUpdatedAt("");
      }
      setDraftHydrated(true);
    }

    hydrateNewDraft();

    return () => {
      active = false;
    };
  }, [location.search, monteurScopeKey, selectedOpnameId]);

  useEffect(() => {
    async function loadContextOptions() {
      try {
        const [clientsRes, projectsRes, workordersRes] = await Promise.all([
          api.get("/clients"),
          api.get("/projects"),
          api.get("/werkbonnen"),
        ]);

        const nextClients = Array.isArray(clientsRes?.data) ? clientsRes.data : [];
        const nextProjects = Array.isArray(projectsRes?.data) ? projectsRes.data : [];
        const nextWorkorders = Array.isArray(workordersRes?.data) ? workordersRes.data : [];

        setClients(nextClients);
        setProjects(nextProjects);
        setWorkorders(nextWorkorders);

        await Promise.all([
          setCachedLookupClients({ scopeKey: monteurScopeKey, payload: nextClients }),
          setCachedLookupProjects({ scopeKey: monteurScopeKey, payload: nextProjects }),
          setCachedLookupWorkorders({ scopeKey: monteurScopeKey, payload: nextWorkorders }),
        ]);
      } catch {
        const [cachedClients, cachedProjects, cachedWorkorders] = await Promise.all([
          getCachedLookupClients({ scopeKey: monteurScopeKey }),
          getCachedLookupProjects({ scopeKey: monteurScopeKey }),
          getCachedLookupWorkorders({ scopeKey: monteurScopeKey }),
        ]);

        setClients(Array.isArray(cachedClients?.payload) ? cachedClients.payload : []);
        setProjects(Array.isArray(cachedProjects?.payload) ? cachedProjects.payload : []);
        setWorkorders(Array.isArray(cachedWorkorders?.payload) ? cachedWorkorders.payload : []);
      }
    }

    loadContextOptions();
  }, [monteurScopeKey]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const opnameId = String(params.get("opnameId") || "").trim();
    if (!opnameId) return;
    openOpname(opnameId);
  }, [location.search, openOpname]);

  useEffect(() => {
    if (!draftHydrated) return;

    void setDraftOpname({
      scopeKey: monteurScopeKey,
      draftId: activeDraftId,
      payload: {
        ...form,
        _id: String(selectedOpnameId || "").trim(),
      },
      meta: {
        opnameId: String(selectedOpnameId || "").trim(),
        title: String(form?.titel || "").trim(),
      },
    }).then((record) => {
      setDraftStatus("draft");
      setDraftUpdatedAt(String(record?.updatedAt || "").trim());
    });
  }, [activeDraftId, draftHydrated, form, monteurScopeKey, selectedOpnameId]);

  useEffect(() => {
    refreshQueueState();
  }, [refreshQueueState]);

  useEffect(() => {
    let active = true;

    async function syncQueuedOpnames() {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        if (active) setSyncStatus("offline");
        return;
      }

      if (active) setSyncStatus("syncing");
      const summary = await runQueuedOpnameSync({
        scopeKey: monteurScopeKey,
        callbacks: {
          onItemSynced: async ({ previousEntityId, savedItem }) => {
            if (!active) return;
            const previousId = String(previousEntityId || "").trim();
            const savedId = String(savedItem?._id || "").trim();
            const selectedId = String(selectedOpnameId || "").trim();

            if (selectedId && (selectedId === previousId || selectedId === savedId)) {
              setSelectedOpnameId(savedId);
              setForm(await hydrateOpnameForForm(savedItem, monteurScopeKey));
              setDraftStatus("idle");
              setDraftUpdatedAt("");
            }
          },
        },
      });

      if (!active) return;

      if (summary.needsAttention > 0) {
        setSyncStatus("needs_attention");
      } else if (summary.failed > 0) {
        setSyncStatus("retry_waiting");
      } else {
        setSyncStatus("idle");
      }
      await Promise.all([refreshQueueState(), loadOpnames()]);
    }

    syncQueuedOpnames();

    function handleOnline() {
      syncQueuedOpnames();
    }

    function handleOffline() {
      if (active) setSyncStatus("offline");
    }

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      active = false;
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [loadOpnames, monteurScopeKey, refreshQueueState, selectedOpnameId]);

  function startNieuweOpname() {
    setSelectedOpnameId("");
    setError("");
    setSuccess("");
    setDraftHydrated(false);
    void getDraftOpname({
      scopeKey: monteurScopeKey,
      draftId: createOpnameDraftId(""),
    }).then(async (draft) => {
      if (draft?.payload) {
        setForm(await hydrateOpnameForForm(draft.payload, monteurScopeKey));
        setDraftStatus("draft");
        setDraftUpdatedAt(String(draft?.updatedAt || "").trim());
      } else {
        setForm(createDefaultOpname());
        setDraftStatus("idle");
        setDraftUpdatedAt("");
      }
      setDraftHydrated(true);
    });
  }

  function updateField(key, value) {
    setForm((prev) => {
      if (key === "clientId") {
        return {
          ...prev,
          clientId: value,
          projectId: "",
          workorderId: "",
        };
      }
      if (key === "projectId") {
        return {
          ...prev,
          projectId: value,
          workorderId: "",
        };
      }
      return { ...prev, [key]: value };
    });
  }

  function updateLocationField(key, value) {
    setForm((prev) => ({
      ...prev,
      locatie: {
        ...(prev.locatie || {}),
        [key]: value,
      },
    }));
  }

  function addItem(type) {
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, createEmptyItem(type, { room: prev?.locatie?.ruimte || "" })],
    }));
  }

  function addConstateringQuick() {
    addItem("constatering");
  }

  function updateItem(itemId, key, value) {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.localId === itemId ? { ...item, [key]: value } : item
      ),
    }));
  }

  function removeItem(itemId) {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.localId !== itemId),
    }));
  }

  function updatePhoto(itemId, photoIndex, key, value) {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.localId === itemId
          ? {
              ...item,
              photos: item.photos.map((photo, index) =>
                index === photoIndex ? { ...photo, [key]: value } : photo
              ),
            }
          : item
      ),
    }));
  }

  function addPhotoPlaceholder(itemId, photoData = {}) {
    const nextPhoto = normalizeOpnamePhoto(photoData);
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.localId === itemId
          ? {
              ...item,
              photos: [...item.photos, nextPhoto],
            }
          : item
      ),
    }));
  }

  async function removePhoto(itemId, photoIndex) {
    const targetPhoto = form.items
      .find((item) => item.localId === itemId)
      ?.photos?.[photoIndex];
    const localPhotoId = String(targetPhoto?.localPhotoId || "").trim();
    if (localPhotoId) {
      await deletePendingOpnamePhoto({ scopeKey: monteurScopeKey, photoId: localPhotoId });
    }

    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.localId === itemId
          ? {
              ...item,
              photos: item.photos.filter((_, index) => index !== photoIndex),
            }
          : item
      ),
    }));
  }

  async function handleUploadPhoto(itemId, event) {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingItemId(itemId);
      setError("");
      const isOnline = typeof navigator === "undefined" ? true : navigator.onLine !== false;
      if (!isOnline) {
        throw new Error("OFFLINE_PHOTO_QUEUE_REQUIRED");
      }

      const data = await uploadOpnamePhoto(file);
      addPhotoPlaceholder(itemId, {
        ...data,
        url: buildStablePhotoUrl(data?.fileId, data?.url || data?.fileUrl || data?.viewUrl || ""),
        previewUrl: resolveAssetUrl(data?.url || data?.fileUrl || data?.viewUrl || ""),
        fileName: file.name,
      });
    } catch (err) {
      const shouldQueue =
        err?.message === "OFFLINE_PHOTO_QUEUE_REQUIRED" || isRetryableUploadError(err);

      if (!shouldQueue) {
        setError(err.message || "Foto uploaden mislukt");
      } else {
        const localPhotoId = createLocalPhotoId();
        await putPendingOpnamePhoto({
          scopeKey: monteurScopeKey,
          photoId: localPhotoId,
          entityId: selectedOpnameId || "",
          blob: file,
          fileName: file.name || "opname-photo.jpg",
          mimeType: file.type || "",
          size: file.size || 0,
        });
        addPhotoPlaceholder(itemId, {
          localPhotoId,
          pendingUpload: true,
          previewUrl: URL.createObjectURL(file),
          caption: "",
          fileName: file.name || "",
        });
        setSuccess("Foto lokaal bewaard en wordt geupload zodra synchronisatie weer mogelijk is.");
      }
    } finally {
      setUploadingItemId("");
      event.target.value = "";
    }
  }

  async function handleSave() {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const payload = serializeForm(form, { includePendingPhotos: true });
      const currentEntityId = String(selectedOpnameId || "").trim();
      const hasServerId = currentEntityId && !isLocalOpnameId(currentEntityId);
      const isOnline = typeof navigator === "undefined" ? true : navigator.onLine !== false;
      let uploadReadyPayload = payload;
      let uploadedLocalPhotoIds = Array.isArray(form?.pendingPhotoCleanupIds)
        ? form.pendingPhotoCleanupIds.map((item) => String(item || "").trim()).filter(Boolean)
        : [];

      try {
        if (!isOnline) {
          throw new Error("OFFLINE_SAVE_REQUIRED");
        }

        const resolvedPhotoUploads = await resolvePendingOpnamePhotoUploads({
          scopeKey: monteurScopeKey,
          entityId: currentEntityId || "new",
          payload,
        });
        uploadReadyPayload = resolvedPhotoUploads.payload;
        uploadedLocalPhotoIds = Array.from(
          new Set([
            ...uploadedLocalPhotoIds,
            ...resolvedPhotoUploads.uploadedLocalPhotoIds.map((item) => String(item || "").trim()),
          ])
        ).filter(Boolean);

        const saved = hasServerId
          ? await updateOpname(currentEntityId, uploadReadyPayload)
          : await createOpname(uploadReadyPayload);

        await cleanupResolvedPendingOpnamePhotos({
          scopeKey: monteurScopeKey,
          uploadedLocalPhotoIds,
        });

        const normalized = normalizeOpname(saved);
        setSelectedOpnameId(String(saved._id));
        setForm({
          ...normalized,
          pendingPhotoCleanupIds: [],
        });
        setSuccess(hasServerId ? "Opname bijgewerkt." : "Opname aangemaakt.");
        await setCachedOpnameDetail({
          scopeKey: monteurScopeKey,
          opnameId: String(saved._id || "").trim(),
          payload: saved,
        });
        await Promise.all([
          deleteDraftOpname({
            scopeKey: monteurScopeKey,
            draftId: createOpnameDraftId(currentEntityId),
          }),
          deleteDraftOpname({
            scopeKey: monteurScopeKey,
            draftId: createOpnameDraftId(String(saved._id || "").trim()),
          }),
          clearQueuedOpnameMutations({
            scopeKey: monteurScopeKey,
            entityId: currentEntityId,
          }),
          clearQueuedOpnameMutations({
            scopeKey: monteurScopeKey,
            entityId: String(saved._id || "").trim(),
          }),
        ]);
        setDraftStatus("idle");
        setDraftUpdatedAt("");
        await Promise.all([refreshQueueState(), loadOpnames()]);
      } catch (err) {
        const shouldQueue =
          err?.message === "OFFLINE_SAVE_REQUIRED" ||
          !err?.status ||
          Number(err?.status || err?.response?.status || 0) >= 500;

        if (!shouldQueue) {
          if (uploadedLocalPhotoIds.length > 0) {
            setForm((prev) => ({
              ...normalizeOpname({
                ...uploadReadyPayload,
                _id: prev?._id || "",
                sourceRequestId:
                  uploadReadyPayload?.sourceRequestId || prev?.sourceRequestId || "",
              }),
              pendingPhotoCleanupIds: uploadedLocalPhotoIds,
            }));
          }
          throw err;
        }

        const localEntityId = currentEntityId || createLocalOpnameId();
        const offlineSnapshot = {
          ...uploadReadyPayload,
          _id: localEntityId,
          aangemaaktDoorNaam: user?.naam || user?.name || user?.email || "",
          createdAt: form?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        await queueOpnameMutation({
          scopeKey: monteurScopeKey,
          tenantId: user?.tenantId || user?.tenant || "",
          userId: user?._id || user?.id || user?.userId || user?.sub || user?.email || "",
          action: hasServerId ? "update" : "create",
          entityId: localEntityId,
          payload: uploadReadyPayload,
          meta: {
            title: uploadReadyPayload?.titel || "",
            projectNummer: uploadReadyPayload?.projectNummer || "",
            pendingPhotoCleanupIds: uploadedLocalPhotoIds,
          },
        });
        await upsertOfflineOpnameSnapshot({
          scopeKey: monteurScopeKey,
          entityId: localEntityId,
          payload: offlineSnapshot,
          offlineStatus: "pending_sync",
        });
        if (!currentEntityId) {
          await deleteDraftOpname({
            scopeKey: monteurScopeKey,
            draftId: createOpnameDraftId(""),
          });
        }

        setSelectedOpnameId(localEntityId);
        setForm((prev) => ({
          ...prev,
          _id: localEntityId,
        }));
        setDraftStatus("draft");
        setSuccess("Opname lokaal opgeslagen en in wachtrij gezet voor synchronisatie.");
        setSyncStatus("retry_waiting");
        await Promise.all([refreshQueueState(), loadOpnames()]);
      }
    } catch (err) {
      setError(err.message || "Opslaan opname mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateWorkorder() {
    if (!selectedOpnameId || !selectedOpname) return;

    const confirmed = window.confirm(
      `Werkbon aanmaken vanuit opname "${selectedOpname.titel || "zonder titel"}"?`
    );
    if (!confirmed) return;

    try {
      setCreatingWorkorder(true);
      setError("");
      setSuccess("");

      const result = await createWorkorderFromOpname(selectedOpnameId, {});
      const workorder = result?.workorder || null;
      const workorderId = String(workorder?._id || "").trim();
      const workorderNummer = String(
        workorder?.werkbonNummer || workorder?.nummer || workorderId
      ).trim();

      await loadOpnames();
      await refreshSelectedOpname(selectedOpnameId);

      setSuccess(`Werkbon aangemaakt vanuit opname: ${workorderNummer}`);

      if (workorderId) {
        const goToWorkorder = window.confirm(
          `Werkbon ${workorderNummer} is aangemaakt. Wil je deze nu openen?`
        );
        if (goToWorkorder) {
          navigate(`/werkbon/${workorderId}`);
        }
      }
    } catch (err) {
      if (
        Number(err?.status || 0) === 400 &&
        String(err?.code || "").trim().toUpperCase() === "VALIDATION_ERROR"
      ) {
        const message =
          err?.message ||
          "Koppel eerst een klant aan deze opname voordat je een werkbon maakt.";
        window.alert(message);
        setError(message);
        return;
      }
      setError(err.message || "Werkbon aanmaken vanuit opname mislukt");
    } finally {
      setCreatingWorkorder(false);
    }
  }

  return (
    <div className="grid min-h-[calc(100vh-7rem)] gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
      <aside className="space-y-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
              Opnames
            </p>
            <h2 className="text-xl font-semibold text-slate-950">Blokkeneditor</h2>
          </div>
          <button
            type="button"
            onClick={startNieuweOpname}
            className="rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
          >
            Nieuw
          </button>
        </div>

        {loading && <p className="text-sm text-slate-500">Opnames laden...</p>}
        {error && <p className="text-sm text-rose-700">{error}</p>}
        {success && <p className="text-sm text-emerald-700">{success}</p>}
        {draftStatus === "draft" && (
          <p className="text-sm text-amber-700">
            Concept lokaal opgeslagen{draftUpdatedAt ? ` (${formatListDate(draftUpdatedAt)})` : ""}.
          </p>
        )}
        {pendingSyncCount > 0 && (
          <p className="text-sm text-cyan-700">
            {pendingSyncCount} wijziging(en) wachten op synchronisatie.
          </p>
        )}

        <div className="space-y-2">
          {opnames.map((opname) => (
            <button
              key={opname._id}
              type="button"
              onClick={() => openOpname(opname._id)}
              className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                String(opname._id) === String(selectedOpnameId)
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-slate-50 text-slate-900 hover:bg-white"
              }`}
            >
              <p className="truncate text-sm font-semibold">{formatOpnameListTitle(opname)}</p>
              <p className="mt-1 text-xs opacity-75">{opname.projectNummer || "Geen projectnummer"}</p>
              <p className="mt-1 truncate text-xs opacity-75">
                Door: {formatOpnameCreator(opname)}
              </p>
              {String(opname?._offlineStatus || "") === "pending_sync" ? (
                <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.16em] opacity-90">
                  Pending sync
                </p>
              ) : null}
            </button>
          ))}

          {!loading && opnames.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
              Nog geen opnames opgeslagen.
            </div>
          )}
        </div>
      </aside>

      <main className="space-y-6">
        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-[linear-gradient(135deg,_#0f172a,_#1e293b_60%,_#334155)] p-6 text-white shadow-xl">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-cyan-200">
                Opnameflow
              </p>
              <h1 className="text-3xl font-semibold tracking-tight">
                Monteurflow met S=CUE: minder keuzes, sneller vastleggen.
              </h1>
              <p className="max-w-2xl text-sm text-slate-200">
                Start met constatering of actiepunt. Meer opties zijn beschikbaar, maar niet meer leidend in beeld.
              </p>
              {draftStatus === "draft" ? (
                <p className="text-sm text-amber-200">Wijzigingen staan lokaal als concept opgeslagen.</p>
              ) : null}
              {syncStatus === "offline" ? (
                <p className="text-sm text-amber-200">Offline: queued wijzigingen worden later verstuurd.</p>
              ) : null}
              {syncStatus === "syncing" ? (
                <p className="text-sm text-cyan-200">Synchronisatie loopt.</p>
              ) : null}
              {syncStatus === "needs_attention" ? (
                <p className="text-sm text-rose-200">Er zijn queue-items die handmatige aandacht nodig hebben.</p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {selectedOpname && canCreateWorkorderFromOpname ? (
                <button
                  type="button"
                  onClick={handleCreateWorkorder}
                  disabled={creatingWorkorder}
                  className="inline-flex items-center gap-2 rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm font-semibold text-cyan-900 hover:bg-cyan-100 disabled:opacity-60"
                >
                  <ArrowRight size={16} />
                  {creatingWorkorder ? "Werkbon maken..." : "Maak werkbon vanuit opname"}
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-100 disabled:opacity-60"
              >
                <Save size={16} />
                {saving ? "Opslaan..." : selectedOpname ? "Opname bijwerken" : "Opname opslaan"}
              </button>
            </div>
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-6">
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-950">Basisgegevens</h3>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <Field label="Titel" value={form.titel} onChange={(value) => updateField("titel", value)} />
                <Field
                  label="Projectnummer"
                  value={form.projectNummer}
                  onChange={(value) => updateField("projectNummer", value)}
                />
                <SelectField
                  label="Klant"
                  value={form.clientId || ""}
                  options={[
                    ["", "Geen gekoppelde klant"],
                    ...clients.map((client) => [
                      String(client?._id || ""),
                      String(client?.naam || client?.name || "Onbekende klant"),
                    ]),
                  ]}
                  onChange={(value) => updateField("clientId", value)}
                />
                <SelectField
                  label="Project"
                  value={form.projectId || ""}
                  options={[
                    ["", !String(form.clientId || "").trim() ? "Kies eerst een klant" : "Geen gekoppeld project"],
                    ...availableProjects.map((project) => [
                      String(project?._id || ""),
                      String(
                        project?.naam ||
                          project?.title ||
                          project?.projectNummer ||
                          project?.number ||
                          "Onbekend project"
                      ),
                    ]),
                  ]}
                  onChange={(value) => updateField("projectId", value)}
                />
                <SelectField
                  label="Werkbon"
                  value={form.workorderId || ""}
                  options={[
                    [
                      "",
                      !String(form.clientId || "").trim() && !String(form.projectId || "").trim()
                        ? "Kies eerst klant of project"
                        : "Geen gekoppelde werkbon",
                    ],
                    ...availableWorkorders.map((workorder) => [
                      String(workorder?._id || ""),
                      String(
                        workorder?.werkbonNummer ||
                          workorder?.nummer ||
                          workorder?.omschrijving ||
                          "Onbekende werkbon"
                      ),
                    ]),
                  ]}
                  onChange={(value) => updateField("workorderId", value)}
                />
                <SelectField
                  label="Discipline"
                  value={form.discipline}
                  options={[
                    ["bmi", "BMI"],
                    ["oai", "OAI"],
                    ["elektra", "Elektra"],
                    ["sturing", "Sturing"],
                    ["hvac", "HVAC"],
                    ["overig", "Overig"],
                  ]}
                  onChange={(value) => updateField("discipline", value)}
                />
                <SelectField
                  label="Aard werk"
                  value={form.aardWerk}
                  options={[
                    ["nieuw", "Nieuw"],
                    ["wijzigen", "Wijzigen"],
                    ["herstel", "Herstel"],
                    ["verwijderen", "Verwijderen"],
                  ]}
                  onChange={(value) => updateField("aardWerk", value)}
                />
                <Field
                  label="Ruimte"
                  value={form.locatie.ruimte}
                  onChange={(value) => updateLocationField("ruimte", value)}
                />
                <Field
                  label="Omgeving"
                  value={form.locatie.omgeving}
                  onChange={(value) => updateLocationField("omgeving", value)}
                />
                <Field
                  label="Bronpunt"
                  value={form.locatie.bronpunt}
                  onChange={(value) => updateLocationField("bronpunt", value)}
                />
                <Field
                  label="Bestemming"
                  value={form.locatie.bestemming}
                  onChange={(value) => updateLocationField("bestemming", value)}
                />
              </div>
              <label className="mt-3 block">
                <span className="mb-1 block text-sm text-slate-600">Omschrijving</span>
                <textarea
                  value={form.omschrijving}
                  onChange={(e) => updateField("omschrijving", e.target.value)}
                  className="min-h-[100px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-slate-400 focus:bg-white"
                />
              </label>
              <label className="mt-3 block">
                <span className="mb-1 block text-sm text-slate-600">Onzekerheden / aannames</span>
                <textarea
                  value={form.onzekerheden}
                  onChange={(e) => updateField("onzekerheden", e.target.value)}
                  className="min-h-[80px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-slate-400 focus:bg-white"
                />
              </label>
            </section>

            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">Opnameblokken</h3>
                  <p className="text-sm text-slate-500">
                    Kernflow: Constatering of Actiepunt. Extra bloktypes staan onder "Meer opties".
                  </p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {primaryTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.key}
                      type="button"
                      onClick={() => addItem(type.key)}
                      className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
                    >
                      <Plus size={14} />
                      <Icon size={14} />
                      {type.label}
                    </button>
                  );
                })}

                <button
                  type="button"
                  onClick={() => setShowMoreTypes((prev) => !prev)}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Plus size={14} />
                  {showMoreTypes ? "Minder opties" : "Meer opties"}
                </button>
              </div>

              {showMoreTypes && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {secondaryTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.key}
                        type="button"
                        onClick={() => addItem(type.key)}
                        className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        <Plus size={14} />
                        <Icon size={14} />
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="mt-3">
                <button
                  type="button"
                  onClick={addConstateringQuick}
                  className="inline-flex items-center gap-2 rounded-2xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"
                >
                  <MessageSquareWarning size={14} />
                  Volgende constatering
                </button>
              </div>

              <div className="mt-5 space-y-4">
                {form.items.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    Nog geen blokken toegevoegd. Start met een constatering.
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={addConstateringQuick}
                        className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-700"
                      >
                        <Plus size={14} />
                        Standaard constatering toevoegen
                      </button>
                    </div>
                  </div>
                )}

                {form.items.map((item, index) => (
                  <OpnameItemCard
                    key={item.localId}
                    item={item}
                    index={index}
                    uploading={uploadingItemId === item.localId}
                    onChange={updateItem}
                    onRemove={removeItem}
                    onPhotoChange={updatePhoto}
                    onPhotoRemove={removePhoto}
                    onPhotoUpload={handleUploadPhoto}
                  />
                ))}
              </div>
              {!String(form.clientId || "").trim() ? (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Koppel eerst een klant aan deze opname voordat je een werkbon maakt.
                </div>
              ) : null}
            </section>
          </div>

          <aside className="space-y-4">
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-950">Preview</h3>
              <div className="mt-4 space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                    {form.projectNummer || "Geen projectnummer"}
                  </p>
                  <h4 className="mt-1 text-lg font-semibold text-slate-950">
                    {form.titel || "Nieuwe opname"}
                  </h4>
                  {selectedOpname ? (
                    <p className="mt-1 text-xs text-slate-500">
                      Opgenomen door: {formatOpnameCreator(selectedOpname)}
                    </p>
                  ) : null}
                  <p className="mt-2 text-sm text-slate-600">{form.omschrijving || "Nog geen omschrijving."}</p>
                </div>

                {selectedOpname ? (
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Werkbonkoppelingen
                    </p>
                    <div className="mt-3 space-y-2">
                      {renderRelatedWorkorderLink(selectedOpname?.workorder)}
                      {Array.isArray(selectedOpname?.derivedWorkorderIds) &&
                      selectedOpname.derivedWorkorderIds.length > 0 ? (
                        selectedOpname.derivedWorkorderIds.map((item, index) => (
                          <div
                            key={`${String(item?._id || item || "")}-${index}`}
                            className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 px-3 py-2"
                          >
                            <div>
                              <p className="text-sm font-medium text-slate-900">
                                {formatDerivedWorkorderLabel(item)}
                              </p>
                              <p className="text-xs text-slate-500">Afgeleide werkbon</p>
                            </div>
                            {resolveDerivedWorkorderId(item) ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/werkbon/${resolveDerivedWorkorderId(item)}`)}
                                className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                              >
                                Open
                              </button>
                            ) : null}
                          </div>
                        ))
                      ) : selectedOpname?.workorder ? null : (
                        <p className="text-sm text-slate-500">Nog geen gekoppelde werkbonnen.</p>
                      )}
                    </div>
                  </div>
                ) : null}

                {form.items.map((item, index) => {
                  const labels = getItemFieldLabels(item.type);
                  return (
                    <div key={`${item.localId}-preview`} className="rounded-2xl border border-slate-200 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Blok {index + 1} · {resolveItemTypeLabel(item.type)}
                      </p>
                      <p className="mt-1 font-semibold text-slate-950">{item.title || "Zonder titel"}</p>
                      {item.question && <p className="mt-2 text-sm text-slate-700"><strong>{labels.questionLabel}:</strong> {item.question}</p>}
                      {item.answer && <p className="mt-1 text-sm text-slate-700"><strong>{labels.answerLabel}:</strong> {item.answer}</p>}
                      {item.desiredSituation && (
                        <p className="mt-1 text-sm text-slate-700">
                          <strong>{labels.desiredLabel}:</strong> {item.desiredSituation}
                        </p>
                      )}
                      {item.notes && <p className="mt-2 text-sm text-slate-600">{item.notes}</p>}
                      {item.photos.length > 0 && (
                        <p className="mt-2 text-xs text-slate-500">{item.photos.length} foto('s) gekoppeld</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </aside>
        </section>
      </main>
    </div>
  );
}

function OpnameItemCard({
  item,
  index,
  uploading,
  onChange,
  onRemove,
  onPhotoChange,
  onPhotoRemove,
  onPhotoUpload,
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const labels = getItemFieldLabels(item.type);

  return (
    <article className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            Blok {index + 1} · {resolveItemTypeLabel(item.type)}
          </p>
          <h4 className="text-base font-semibold text-slate-950">{item.title || "Nieuw blok"}</h4>
        </div>
        <button
          type="button"
          onClick={() => onRemove(item.localId)}
          className="rounded-2xl border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
        >
          Verwijder
        </button>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <Field label="Titel" value={item.title} onChange={(value) => onChange(item.localId, "title", value)} />
        <Field label="Ruimte" value={item.room} onChange={(value) => onChange(item.localId, "room", value)} />
      </div>

      {item.type !== "foto" && (
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <Field
            label={labels.questionLabel}
            value={item.question}
            onChange={(value) => onChange(item.localId, "question", value)}
          />
          <Field
            label={labels.answerLabel}
            value={item.answer}
            onChange={(value) => onChange(item.localId, "answer", value)}
          />
        </div>
      )}

      <label className="mt-3 block">
        <span className="mb-1 block text-sm text-slate-600">{labels.desiredLabel}</span>
        <textarea
          value={item.desiredSituation}
          onChange={(e) => onChange(item.localId, "desiredSituation", e.target.value)}
          className="min-h-[80px] w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-slate-400"
        />
      </label>

      <label className="mt-3 block">
        <span className="mb-1 block text-sm text-slate-600">{labels.notesLabel}</span>
        <textarea
          value={item.notes}
          onChange={(e) => onChange(item.localId, "notes", e.target.value)}
          className="min-h-[80px] w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-slate-400"
        />
      </label>

      <div className="mt-3">
        <button
          type="button"
          onClick={() => setShowAdvanced((prev) => !prev)}
          className="rounded-2xl border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100"
        >
          {showAdvanced ? "Minder instellingen" : "Meer instellingen"}
        </button>
      </div>

      {showAdvanced && (
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <SelectField
            label="Status"
            value={item.status}
            options={[
              ["open", "Open"],
              ["akkoord", "Akkoord"],
              ["nodig", "Nodig"],
              ["nvt", "N.v.t."],
            ]}
            onChange={(value) => onChange(item.localId, "status", value)}
          />
          <ToggleField
            label="Verplicht punt"
            checked={item.required}
            onChange={(value) => onChange(item.localId, "required", value)}
          />
        </div>
      )}

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Foto's</p>
            <p className="text-xs text-slate-500">Upload bewijsfoto's of situatiebeelden voor dit blok.</p>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <ImagePlus size={16} />
            {uploading ? "Uploaden..." : "Foto toevoegen"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(event) => onPhotoUpload(item.localId, event)}
              disabled={uploading}
            />
          </label>
        </div>

        <div className="mt-4 space-y-3">
          {item.photos.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-500">
              Nog geen foto's gekoppeld.
            </div>
          )}
          {item.photos.map((photo, photoIndex) => (
            <div key={`${item.localId}-photo-${photoIndex}`} className="grid gap-3 md:grid-cols-[140px_minmax(0,1fr)_auto]">
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
                <OpnamePhotoPreview photo={photo} />
              </div>
              <div className="space-y-2">
                {photo.pendingUpload ? (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    Foto wacht op synchronisatie{photo.fileName ? `: ${photo.fileName}` : ""}.
                  </div>
                ) : (
                  <Field
                    label="Foto URL"
                    value={photo.url}
                    onChange={(value) => onPhotoChange(item.localId, photoIndex, "url", value)}
                  />
                )}
                <Field
                  label="Bijschrift"
                  value={photo.caption}
                  onChange={(value) => onPhotoChange(item.localId, photoIndex, "caption", value)}
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => onPhotoRemove(item.localId, photoIndex)}
                  className="rounded-2xl border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                >
                  Verwijder
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}

function OpnamePhotoPreview({ photo }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);

  const previewSource = photo?.previewUrl || resolveAssetUrl(photo?.url);

  useEffect(() => {
    let active = true;
    let objectUrl = "";

    async function loadSecurePreview() {
      if (!isSecureFileUrl(photo?.url)) {
        setBlobUrl("");
        setLoadFailed(false);
        return;
      }

      try {
        setLoadFailed(false);
        const secureUrl = resolveAssetUrl(photo.url);
        const res = await api.get(secureUrl, { responseType: "blob" });
        objectUrl = URL.createObjectURL(res.data);
        if (!active) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setBlobUrl(objectUrl);
      } catch {
        if (active) {
          setBlobUrl("");
          setLoadFailed(true);
        }
      }
    }

    loadSecurePreview();

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [photo?.url]);

  if (!photo?.url && !photo?.previewUrl) {
    return <div className="flex h-28 items-center justify-center text-xs text-slate-400">Geen foto</div>;
  }

  if (isSecureFileUrl(photo?.url) && !blobUrl && loadFailed) {
    return (
      <div className="flex h-28 items-center justify-center px-3 text-center text-xs text-slate-400">
        Foto kon niet geladen worden.
      </div>
    );
  }

  const src = blobUrl || previewSource;
  if (!src) {
    return <div className="flex h-28 items-center justify-center text-xs text-slate-400">Geen foto</div>;
  }

  return (
    <img
      src={src}
      alt={photo.caption || "Opnamefoto"}
      onError={(event) => {
        const fallback = resolveAssetUrl(photo.url);
        if (fallback && event.currentTarget.src !== fallback) {
          event.currentTarget.src = fallback;
        }
      }}
      className="h-28 w-full object-cover"
    />
  );
}

function Field({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-slate-600">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-slate-400 focus:bg-white"
      />
    </label>
  );
}

function SelectField({ label, value, options, onChange }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-slate-600">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-slate-400 focus:bg-white"
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </label>
  );
}

function ToggleField({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function getItemFieldLabels(type) {
  const normalized = String(type || "").trim().toLowerCase();

  if (["constatering", "actiepunt"].includes(normalized)) {
    return {
      questionLabel: "S - Situatie",
      answerLabel: "C - Cause / Oorzaak",
      desiredLabel: "U - Uitvoering / Actie",
      notesLabel: "E - Evaluatie / Resultaat",
    };
  }

  if (normalized === "vraag") {
    return {
      questionLabel: "Vraag",
      answerLabel: "Antwoord",
      desiredLabel: "Gewenste situatie",
      notesLabel: "Notities",
    };
  }

  if (normalized === "checklist") {
    return {
      questionLabel: "Checklistpunt",
      answerLabel: "Uitkomst",
      desiredLabel: "Vervolgactie",
      notesLabel: "Notities",
    };
  }

  if (normalized === "foto") {
    return {
      questionLabel: "Omschrijving",
      answerLabel: "Toelichting",
      desiredLabel: "Wat moet zichtbaar zijn",
      notesLabel: "Notities bij foto",
    };
  }

  return {
    questionLabel: "Vraag / constatering",
    answerLabel: "Antwoord / keuze",
    desiredLabel: "Moet hier komen / gewenste situatie",
    notesLabel: "Notities",
  };
}

function formatListDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function formatOpnameListTitle(opname) {
  const title = String(opname?.titel || "").trim();
  if (!title) return "Zonder titel";
  if (title.toLowerCase() !== "nieuwe opname") return title;

  const stamp = formatListDate(opname?.createdAt);
  return stamp ? `Nieuwe opname (${stamp})` : title;
}

function formatOpnameCreator(opname) {
  const name = String(opname?.aangemaaktDoorNaam || "").trim();
  if (name) return name;

  const creator = opname?.aangemaaktDoor;
  if (creator && typeof creator === "object") {
    const objectName = String(creator?.naam || creator?.name || creator?.email || "").trim();
    if (objectName) return objectName;
  }

  const email = String(opname?.aangemaaktDoorEmail || "").trim();
  if (email) return email;
  return "Onbekend";
}

function getRoleNames(user) {
  const roleSet = new Set();
  if (user?.rol) roleSet.add(String(user.rol).trim().toLowerCase());
  if (user?.role) roleSet.add(String(user.role).trim().toLowerCase());

  for (const role of user?.roles || []) {
    if (!role) continue;
    if (typeof role === "string") {
      roleSet.add(role.trim().toLowerCase());
      continue;
    }
    if (typeof role === "object") {
      const name = role.name || role.naam;
      if (name) roleSet.add(String(name).trim().toLowerCase());
    }
  }

  return Array.from(roleSet);
}

function resolveDerivedWorkorderId(item) {
  if (!item) return "";
  if (typeof item === "string") return String(item).trim();
  return String(item?._id || item?.workorderId || item?.id || "").trim();
}

function formatDerivedWorkorderLabel(item) {
  if (!item) return "Onbekende werkbon";
  if (typeof item === "string") return item;
  return (
    String(item?.werkbonNummer || item?.nummer || item?.title || item?._id || "").trim() ||
    "Onbekende werkbon"
  );
}

function renderRelatedWorkorderLink(workorder) {
  const workorderId = String(workorder?._id || "").trim();
  if (!workorderId) return null;

  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 px-3 py-2">
      <div>
        <p className="text-sm font-medium text-slate-900">
          {String(workorder?.werkbonNummer || workorder?.nummer || workorderId).trim()}
        </p>
        <p className="text-xs text-slate-500">Primair gekoppelde werkbon</p>
      </div>
      <a
        href={`/werkbon/${workorderId}`}
        className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
      >
        Open
      </a>
    </div>
  );
}

function buildDefaultOpnameTitle() {
  const stamp = formatListDate(new Date());
  return stamp ? `Nieuwe opname (${stamp})` : "Nieuwe opname";
}

function createEmptyItem(type, defaults = {}) {
  const normalizedType = String(type || "constatering").trim().toLowerCase();
  const room = String(defaults?.room || "").trim();

  const defaultTitleByType = {
    constatering: "Constatering",
    actiepunt: "Actiepunt",
    vraag: "Vraag",
    checklist: "Checklist",
    foto: "Foto",
  };

  return {
    localId: cryptoRandomId(),
    type: normalizedType,
    title: defaultTitleByType[normalizedType] || "",
    room,
    question: "",
    answer: "",
    desiredSituation: "",
    notes: "",
    status: "open",
    required: false,
    photos: [],
  };
}

function normalizeOpname(data) {
  const normalized = {
    _id: String(data?._id || "").trim(),
    pendingPhotoCleanupIds: Array.isArray(data?.pendingPhotoCleanupIds)
      ? data.pendingPhotoCleanupIds.map((item) => String(item || "").trim()).filter(Boolean)
      : [],
    sourceRequestId: String(data?.sourceRequestId || "").trim(),
    titel: data?.titel || "",
    omschrijving: data?.omschrijving || "",
    projectNummer: data?.projectNummer || "",
    discipline: data?.discipline || "overig",
    aardWerk: data?.aardWerk || "nieuw",
    uitvoervorm: data?.uitvoervorm || "onbekend",
    locatie: {
      bronpunt: data?.locatie?.bronpunt || "",
      bestemming: data?.locatie?.bestemming || "",
      ruimte: data?.locatie?.ruimte || "",
      omgeving: data?.locatie?.omgeving || "",
    },
    onzekerheden: data?.onzekerheden || "",
    status: data?.status || "concept",
    clientId: String(data?.client?._id || data?.clientId || "").trim(),
    projectId: String(data?.project?._id || data?.projectId || "").trim(),
    workorderId: String(data?.workorder?._id || data?.workorderId || data?.gekoppeldeWerkbonId || "").trim(),
    workorder: data?.workorder || null,
    derivedWorkorderIds: Array.isArray(data?.derivedWorkorderIds) ? data.derivedWorkorderIds : [],
    items: Array.isArray(data?.items)
      ? data.items.map((item) => ({
          localId: item?._id || cryptoRandomId(),
          type: item?.type || "constatering",
          title: item?.title || "",
          room: item?.room || "",
          question: item?.question || "",
          answer: item?.answer || "",
          desiredSituation: item?.desiredSituation || "",
          notes: item?.notes || "",
          status: item?.status || "open",
          required: item?.required === true,
          photos: Array.isArray(item?.photos)
            ? item.photos.map((photo) => normalizeOpnamePhoto(photo))
            : [],
        }))
      : [],
  };

  if (!normalized.items.length) {
    normalized.items = [createEmptyItem("constatering", { room: normalized?.locatie?.ruimte || "" })];
  }

  return normalized;
}

function serializeForm(form, options = {}) {
  return serializeFormWithOptions(form, options);
}

function serializeFormWithOptions(form, options = {}) {
  const includePendingPhotos = options?.includePendingPhotos === true;
  return {
    sourceRequestId:
      String(form.sourceRequestId || "").trim() || createOpnameClientMutationId(),
    titel: String(form.titel || "").trim() || buildDefaultOpnameTitle(),
    omschrijving: String(form.omschrijving || "").trim(),
    projectNummer: String(form.projectNummer || "").trim(),
    clientId: String(form.clientId || "").trim() || undefined,
    projectId: String(form.projectId || "").trim() || undefined,
    workorderId: String(form.workorderId || "").trim() || undefined,
    discipline: form.discipline,
    aardWerk: form.aardWerk,
    uitvoervorm: form.uitvoervorm,
    locatie: {
      bronpunt: String(form?.locatie?.bronpunt || "").trim(),
      bestemming: String(form?.locatie?.bestemming || "").trim(),
      ruimte: String(form?.locatie?.ruimte || "").trim(),
      omgeving: String(form?.locatie?.omgeving || "").trim(),
    },
    onzekerheden: String(form.onzekerheden || "").trim(),
    status: form.status || "concept",
    items: form.items.map((item) => ({
      type: item.type,
      title: String(item.title || "").trim(),
      room: String(item.room || "").trim(),
      question: String(item.question || "").trim(),
      answer: String(item.answer || "").trim(),
      desiredSituation: String(item.desiredSituation || "").trim(),
      notes: String(item.notes || "").trim(),
      status: item.status || "open",
      required: item.required === true,
      photos: item.photos
        .map((photo) => ({
          fileId: String(photo.fileId || "").trim(),
          url: String(photo.url || photo.fileUrl || "").trim(),
          localPhotoId: includePendingPhotos ? String(photo.localPhotoId || "").trim() : "",
          pendingUpload: includePendingPhotos ? photo.pendingUpload === true : false,
          fileName: includePendingPhotos ? String(photo.fileName || "").trim() : "",
          caption: String(photo.caption || "").trim(),
        }))
        .filter((photo) => photo.fileId || photo.url || (includePendingPhotos && photo.localPhotoId)),
    })),
  };
}

function resolveItemTypeLabel(type) {
  return ITEM_TYPES.find((item) => item.key === type)?.label || type;
}

function cryptoRandomId() {
  return Math.random().toString(36).slice(2, 10);
}
